/**
 * Eksport roznic FM2 ↔ FM1 do Excela.
 *
 * Funkcje sa czyste — arkusze skladamy z danych, wywolanie `exportToExcel`
 * zostaje w komponencie. Dzieki temu ksztalt pliku da sie przetestowac bez DOM-u
 * i bez generowania XLSX-a.
 */
import type { Fm1Comparison, SfySetDiff } from "@/features/outbound/outbound-api";
import type { ExcelColumn, ExcelSheet } from "@/lib/export-excel";

/** Etykieta apteki z backendu to `"866691 — Apteka Sloneczna"` albo sam kod. */
export function splitPharmacyLabel(label: string): { idfirm: string; nazwa: string } {
	const i = label.indexOf(" — ");
	if (i < 0) return { idfirm: label.trim(), nazwa: "" };
	return { idfirm: label.slice(0, i).trim(), nazwa: label.slice(i + 3).trim() };
}

/**
 * Po ktorej stronie brakuje pozycji. Slownie, nie skrotem — plik ogladaja
 * osoby, ktore nie znaja naszego nazewnictwa kolumn na ekranie.
 */
const STRONA_FM1 = "Tylko w FM1 — nie przenieslismy";
const STRONA_FM2 = "Tylko w FM2 — nadmiar wzgledem FM1";

const KOL_APTEKI: ExcelColumn[] = [
	{ id: "strona", header: "Strona", widthPx: 260, isNumeric: false },
	{ id: "idfirm", header: "Idfirm", widthPx: 90, isNumeric: false },
	{ id: "nazwa", header: "Nazwa apteki", widthPx: 380, isNumeric: false },
];

const KOL_BLOZY: ExcelColumn[] = [
	{ id: "strona", header: "Strona", widthPx: 260, isNumeric: false },
	{ id: "bloz07", header: "Bloz07", widthPx: 110, isNumeric: false },
];

const KOL_PODSUMOWANIE: ExcelColumn[] = [
	{ id: "pozycja", header: "Pozycja", widthPx: 300, isNumeric: false },
	{ id: "wartosc", header: "Wartosc", widthPx: 520, isNumeric: false },
];

/**
 * Idfirm i bloz zostaja TEKSTEM (`isNumeric: false`) celowo: jako liczby Excel
 * zjadlby wiodace zera (`0649051` → `649051`) i pokazal kody w notacji
 * naukowej — a to sa identyfikatory, nie wartosci do liczenia.
 */
export function buildFm1CompareSheets(cmp: Fm1Comparison): ExcelSheet[] {
	const apteki = [
		...cmp.pharmacies.onlyFm1.map((l) => ({ strona: STRONA_FM1, ...splitPharmacyLabel(l) })),
		...cmp.pharmacies.onlyFm2.map((l) => ({ strona: STRONA_FM2, ...splitPharmacyLabel(l) })),
	];
	const blozy = [
		...cmp.blozy.onlyFm1.map((b) => ({ strona: STRONA_FM1, bloz07: b })),
		...cmp.blozy.onlyFm2.map((b) => ({ strona: STRONA_FM2, bloz07: b })),
	];

	const licznik = (etykieta: string, d: SfySetDiff) => [
		{ pozycja: `${etykieta} — wspolnych`, wartosc: String(d.common) },
		{ pozycja: `${etykieta} — tylko w FM1`, wartosc: String(d.onlyFm1Count) },
		{ pozycja: `${etykieta} — tylko w FM2`, wartosc: String(d.onlyFm2Count) },
	];

	const podsumowanie: Record<string, unknown>[] = [
		{ pozycja: "Kontrakt FM2", wartosc: cmp.fm2ContractName },
		{ pozycja: "Kontrakt FM1", wartosc: cmp.fm1ContractName ?? `#${cmp.fm1ContractId}` },
		...licznik("Apteki", cmp.pharmacies),
		...licznik("Blozy", cmp.blozy),
		...cmp.notes.map((n, i) => ({ pozycja: `Uwaga ${i + 1}`, wartosc: n })),
	];

	// Ostrzezenie w pliku, nie tylko na ekranie: jesli backend jednak obcial
	// listy, czytajacy plik musi wiedziec, ze nie ma kompletu.
	if (cmp.pharmacies.truncated || cmp.blozy.truncated) {
		podsumowanie.push({
			pozycja: "UWAGA",
			wartosc: "Listy zostaly obciete — plik NIE zawiera kompletu roznic.",
		});
	}

	return [
		{ name: "Podsumowanie", columns: KOL_PODSUMOWANIE, rows: podsumowanie },
		{ name: "Apteki", columns: KOL_APTEKI, rows: apteki },
		{ name: "Blozy", columns: KOL_BLOZY, rows: blozy },
	];
}

/** Nazwa pliku bez znakow, ktore psuja pobieranie na Windowsie. */
export function fm1CompareFileName(cmp: Fm1Comparison): string {
	const czysc = (s: string) => s.replace(/[\\/:*?"<>|]+/g, "-").trim();
	const fm1 = cmp.fm1ContractName ?? `FM1-${cmp.fm1ContractId}`;
	return `porownanie-fm1_${czysc(cmp.fm2ContractName)}_${czysc(fm1)}.xlsx`;
}
